import { Box, Flex, Input, InputGroup, InputLeftElement, Text, VStack } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import {
    Button,
    CheckboxMenuRow,
    Icon,
    Modal,
} from '../../../ui';

export interface SettingsOverridesBlockProps {
    /** Sub-heading, styled to match a SettingsRow label. */
    title: string;
    description: string;
    addButtonText: string;
    onAddClick: () => void;
    isDisabled?: boolean;
    children: ReactNode;
}

/**
 * Title + description on the left, primary Add button on the right, then an
 * inset child (usually SettingsSubTable). Used for optional override tables
 * that sit under a SettingsCard next to the global setting they override.
 */
export function SettingsOverridesBlock({
    title,
    description,
    addButtonText,
    onAddClick,
    isDisabled,
    children,
}: SettingsOverridesBlockProps) {
    // py matches SettingsRow so this block lines up with the rows above it
    // inside the shared card.
    return (
        <Box py={4}>
            <Flex align="flex-start" justify="space-between" gap={6} mb={4}>
                <Box flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="medium" color="fg.default">
                        {title}
                    </Text>
                    <Text fontSize="sm" color="fg.muted" mt={0.5}>
                        {description}
                    </Text>
                </Box>
                <Box flexShrink={0}>
                    <Button
                        colorScheme="brand"
                        variant="solid"
                        size="sm"
                        onClick={onAddClick}
                        isDisabled={isDisabled}
                    >
                        {addButtonText}
                    </Button>
                </Box>
            </Flex>
            {children}
        </Box>
    );
}

export interface SettingsAddItemsModalItem {
    id: string;
    label: string;
}

export interface SettingsAddItemsModalProps {
    isOpen: boolean;
    onClose: () => void;
    modalTitle: string;
    searchPlaceholder: string;
    searchTerm: string;
    onSearchTermChange: (value: string) => void;
    emptySearchLabel: string;
    items: Array<SettingsAddItemsModalItem>;
    pendingIds: Set<string>;
    onTogglePending: (id: string) => void;
    selectedCountLabel: string | null;
    onAdd: () => void;
}

/**
 * Multi-select add modal with search + checkboxes. Shared by make overrides
 * and Acceptable Margin by part type so the picker chrome stays identical.
 */
export function SettingsAddItemsModal({
    isOpen,
    onClose,
    modalTitle,
    searchPlaceholder,
    searchTerm,
    onSearchTermChange,
    emptySearchLabel,
    items,
    pendingIds,
    onTogglePending,
    selectedCountLabel,
    onAdd,
}: SettingsAddItemsModalProps) {
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            title={modalTitle}
            footer={
                <>
                    <Button size="action" variant="outlineGray" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        size="action"
                        variant="solid"
                        colorScheme="brand"
                        onClick={onAdd}
                        isDisabled={pendingIds.size === 0}
                    >
                        Add Selected
                    </Button>
                </>
            }
        >
            <VStack spacing={3} align="stretch" py={1}>
                <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                        <Icon name="search" size="sm" color="fg.muted" />
                    </InputLeftElement>
                    <Input
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => onSearchTermChange(e.target.value)}
                        bg="bg.surface"
                        borderColor="border.default"
                    />
                </InputGroup>
                <Box
                    border="1px solid"
                    borderColor="border.default"
                    borderRadius="md"
                    maxH="340px"
                    overflowY="auto"
                    p={2}
                >
                    {items.length === 0 ? (
                        <Text fontSize="sm" color="fg.muted" py={4} textAlign="center">
                            {emptySearchLabel}
                        </Text>
                    ) : (
                        <VStack align="stretch" spacing={1}>
                            {items.map((item) => (
                                <CheckboxMenuRow
                                    key={item.id}
                                    isSelected={pendingIds.has(item.id)}
                                    onClick={() => onTogglePending(item.id)}
                                >
                                    {item.label}
                                </CheckboxMenuRow>
                            ))}
                        </VStack>
                    )}
                </Box>
                {selectedCountLabel && (
                    <Text fontSize="xs" color="fg.muted">
                        {selectedCountLabel}
                    </Text>
                )}
            </VStack>
        </Modal>
    );
}

export default SettingsOverridesBlock;
